import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { createUseStyles } from "react-jss";
import { hapticTap } from "../helpers/haptics";

const ANSWER_COLORS = {
  correct: {
    main: "#0F6E56",
    bg: "#E1F5EE"
  },
  wrong: {
    main: "#993C1D",
    bg: "#FAECE7"
  },
  clear: {
    main: "#854F0B",
    bg: "#FAEEDA"
  }
};

const useStyles = createUseStyles({
  buttonRow: {
    display: "grid",
    gridAutoColumns: "minmax(0, 1fr)",
    gridAutoFlow: "column",
    gap: "10px"
  },
  bottomBar: {
    flexShrink: 0,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    padding: "4px 0 0",
    touchAction: "manipulation"
  },
  bottomButton: {
    minHeight: "44px !important",
    flexDirection: "column !important",
    gap: "2px !important",
    fontSize: "12px !important",
    fontWeight: "600 !important",
    textTransform: "none !important",
    "& svg": {
      fontSize: "20px !important"
    }
  },
  wrongButton: {
    borderColor: `${ANSWER_COLORS.wrong.main} !important`,
    color: `${ANSWER_COLORS.wrong.main} !important`,
    "&:hover": {
      borderColor: `${ANSWER_COLORS.wrong.main} !important`,
      backgroundColor: `${ANSWER_COLORS.wrong.bg} !important`
    }
  },
  wrongButtonActive: {
    backgroundColor: `${ANSWER_COLORS.wrong.bg} !important`,
    borderColor: `${ANSWER_COLORS.wrong.main} !important`,
    color: `${ANSWER_COLORS.wrong.main} !important`,
    boxShadow: "none !important",
    "&:hover": {
      backgroundColor: `${ANSWER_COLORS.wrong.bg} !important`,
      boxShadow: "none !important"
    }
  },
  clearButton: {
    borderColor: `${ANSWER_COLORS.clear.main} !important`,
    color: `${ANSWER_COLORS.clear.main} !important`,
    "&:hover": {
      borderColor: `${ANSWER_COLORS.clear.main} !important`,
      backgroundColor: `${ANSWER_COLORS.clear.bg} !important`
    }
  },
  correctButton: {
    borderColor: `${ANSWER_COLORS.correct.main} !important`,
    color: `${ANSWER_COLORS.correct.main} !important`,
    "&:hover": {
      borderColor: `${ANSWER_COLORS.correct.main} !important`,
      backgroundColor: `${ANSWER_COLORS.correct.bg} !important`
    }
  },
  correctButtonActive: {
    backgroundColor: `${ANSWER_COLORS.correct.bg} !important`,
    borderColor: `${ANSWER_COLORS.correct.main} !important`,
    color: `${ANSWER_COLORS.correct.main} !important`,
    boxShadow: "none !important",
    "&:hover": {
      backgroundColor: `${ANSWER_COLORS.correct.bg} !important`,
      boxShadow: "none !important"
    }
  }
});

const AnswerButtons = ({
  answer,
  setAnswer,
  showClearButton,
  variant = "inline",
  onMarkWrong,
  onMarkCorrect
}: {
  answer: boolean | null | undefined;
  setAnswer: React.Dispatch<React.SetStateAction<boolean | null | undefined>>;
  showClearButton: boolean;
  variant?: "inline" | "bottomBar";
  onMarkWrong?: () => void;
  onMarkCorrect?: () => void;
}) => {
  const classes = useStyles();
  const isBottomBar = variant === "bottomBar";

  const handleWrong = () => {
    if (onMarkWrong) {
      onMarkWrong();
      return;
    }
    hapticTap();
    setAnswer(false);
  };

  const handleClear = () => {
    hapticTap();
    setAnswer(null);
  };

  const handleCorrect = () => {
    if (onMarkCorrect) {
      onMarkCorrect();
      return;
    }
    hapticTap();
    setAnswer(true);
  };

  const buttons = (
    <>
      <Button
        aria-label="wrong"
        tabIndex={-1}
        variant={answer === false ? "contained" : "outlined"}
        className={`${answer === false ? classes.wrongButtonActive : classes.wrongButton} ${
          isBottomBar ? classes.bottomButton : ""
        }`}
        onClick={handleWrong}>
        <ClearIcon fontSize={isBottomBar ? "medium" : "small"} />
        {isBottomBar ? "Wrong" : null}
      </Button>
      {showClearButton ? (
        <Button
          aria-label="clear"
          tabIndex={-1}
          variant="outlined"
          className={`${classes.clearButton} ${isBottomBar ? classes.bottomButton : ""}`}
          onClick={handleClear}>
          <HelpOutlineIcon fontSize={isBottomBar ? "medium" : "small"} />
          {isBottomBar ? "Skip" : null}
        </Button>
      ) : isBottomBar ? (
        <Box />
      ) : null}
      <Button
        aria-label="correct"
        tabIndex={-1}
        variant={answer === true ? "contained" : "outlined"}
        className={`${answer === true ? classes.correctButtonActive : classes.correctButton} ${
          isBottomBar ? classes.bottomButton : ""
        }`}
        onClick={handleCorrect}>
        <CheckIcon fontSize={isBottomBar ? "medium" : "small"} />
        {isBottomBar ? "Right" : null}
      </Button>
    </>
  );

  if (isBottomBar) {
    return (
      <div
        className={classes.bottomBar}
        style={{
          gridTemplateColumns: showClearButton ? "1fr 1fr 1fr" : "1fr 1fr"
        }}>
        <Button
          aria-label="wrong"
          tabIndex={-1}
          variant={answer === false ? "contained" : "outlined"}
          className={`${answer === false ? classes.wrongButtonActive : classes.wrongButton} ${classes.bottomButton}`}
          onClick={handleWrong}>
          <ClearIcon fontSize="medium" />
          Wrong
        </Button>
        {showClearButton ? (
          <Button
            aria-label="clear"
            tabIndex={-1}
            variant="outlined"
            className={`${classes.clearButton} ${classes.bottomButton}`}
            onClick={handleClear}>
            <HelpOutlineIcon fontSize="medium" />
            Skip
          </Button>
        ) : null}
        <Button
          aria-label="correct"
          tabIndex={-1}
          variant={answer === true ? "contained" : "outlined"}
          className={`${answer === true ? classes.correctButtonActive : classes.correctButton} ${classes.bottomButton}`}
          onClick={handleCorrect}>
          <CheckIcon fontSize="medium" />
          Right
        </Button>
      </div>
    );
  }

  return <div className={classes.buttonRow}>{buttons}</div>;
};

export default AnswerButtons;
